import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

/** Injection seam so tests can pass a fake Storage; defaults to localStorage. */
export const PLAYBACK_STORAGE = new InjectionToken<Storage | null>('PLAYBACK_STORAGE', {
  providedIn: 'root',
  factory: () => (typeof localStorage !== 'undefined' ? localStorage : null),
});

/** Injection seam for the clock so tests can drive the per-episode save throttle. */
export const PLAYBACK_CLOCK = new InjectionToken<() => number>('PLAYBACK_CLOCK', {
  providedIn: 'root',
  factory: () => () => Date.now(),
});

/**
 * The key a playhead belongs to: the episode, unless a spoken translation is playing.
 *
 * An episode can now be heard in more than one language, and those recordings differ in
 * length — resuming the English version at the position reached in the German one would drop
 * the listener at a meaningless timestamp. The original keeps the bare episode id, so
 * positions saved before translations existed still resolve.
 */
export function playbackTrackKey(episode: {
  id?: string | null;
  sourceLocale?: string | null;
  audioLocale?: string | null;
} | null | undefined): string {
  const id = episode?.id ?? '';
  const audioLocale = episode?.audioLocale ?? '';
  const sourceLocale = episode?.sourceLocale ?? '';
  if (!id || !audioLocale || audioLocale.toLowerCase() === sourceLocale.toLowerCase()) {
    return id;
  }
  return `${id}::${audioLocale.toLowerCase()}`;
}

/**
 * Persists per-episode playback position (seconds) so reopening an episode resumes
 * where the user left off — independent of PiP. Backed by localStorage with an
 * in-memory mirror; degrades to in-memory only when storage is unavailable.
 */
@Injectable({ providedIn: 'root' })
export class PlaybackPositionService {
  private readonly storageKey = 'hypecast.playbackPositions';
  private readonly maxEntries = 200;
  private readonly minSeconds = 5;
  private readonly endGuardSeconds = 10;
  // Per-episode rate-limit for periodic position writes. The service owns this so
  // the multiple playback sources (inline Howl, <video>, mini-player Howl) each
  // report freely without sharing a single timestamp (which used to drop the
  // truthful writer right after a video/audio switch). flush() bypasses it.
  private readonly saveThrottleMs = 4000;
  private lastSaveAt = new Map<string, number>();

  // insertion-ordered map (Map preserves order) → easy oldest-eviction.
  private positions = new Map<string, number>();

  constructor(
    @Optional() @Inject(PLAYBACK_STORAGE) private storage: Storage | null,
    @Optional() @Inject(PLAYBACK_CLOCK) private clock: () => number = () => Date.now(),
  ) {
    this.load();
  }

  /**
   * Save the position for an episode. Throttled per-episode for periodic writes;
   * clears near end-of-media; ignores too-early positions. Use flush() to bypass
   * the throttle (teardown / the moment of a source switch).
   */
  save(episodeId: string, seconds: number, duration: number): void {
    this.write(episodeId, seconds, duration, false);
  }

  /** Like save() but bypasses the throttle so the latest position always lands. */
  flush(episodeId: string, seconds: number, duration: number): void {
    this.write(episodeId, seconds, duration, true);
  }

  /**
   * Authoritative external position (e.g. the continue-in-browser handoff's ?pos=):
   * stored unconditionally, bypassing the min-guard that protects periodic saves
   * from trivial positions — a handoff at 3s must resume at 3s, not restart.
   */
  seed(episodeId: string, seconds: number): void {
    const floored = Math.floor(seconds);
    if (!episodeId || !(floored > 0)) return;
    this.lastSaveAt.set(episodeId, this.clock());
    this.positions.delete(episodeId);
    this.positions.set(episodeId, floored);
    while (this.positions.size > this.maxEntries) {
      const oldest = this.positions.keys().next().value as string | undefined;
      if (oldest === undefined) break;
      this.positions.delete(oldest);
    }
    this.persist();
  }

  private write(episodeId: string, seconds: number, duration: number, force: boolean): void {
    if (!episodeId) return;
    // End-guard and min-guard are state decisions — they apply regardless of throttle.
    if (duration > 0 && seconds >= duration - this.endGuardSeconds) {
      this.lastSaveAt.delete(episodeId);
      this.clear(episodeId);
      return;
    }
    if (seconds < this.minSeconds) return;

    if (!force) {
      const now = this.clock();
      const last = this.lastSaveAt.get(episodeId);
      if (last !== undefined && now - last < this.saveThrottleMs) {
        return; // a recent periodic write already landed for this episode
      }
      this.lastSaveAt.set(episodeId, now);
    } else {
      this.lastSaveAt.set(episodeId, this.clock());
    }

    this.positions.delete(episodeId);
    this.positions.set(episodeId, Math.floor(seconds));
    while (this.positions.size > this.maxEntries) {
      const oldest = this.positions.keys().next().value as string | undefined;
      if (oldest === undefined) break;
      this.positions.delete(oldest);
    }
    this.persist();
  }

  /** Stored position (seconds) for an episode, or 0. */
  get(episodeId: string): number {
    return this.positions.get(episodeId) ?? 0;
  }

  /** Forget an episode's position. */
  clear(episodeId: string): void {
    if (this.positions.delete(episodeId)) {
      this.persist();
    }
  }

  private load(): void {
    if (!this.storage) return;
    try {
      const raw = this.storage.getItem(this.storageKey);
      if (!raw) return;
      const obj = JSON.parse(raw) as Record<string, number>;
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'number' && v > 0) this.positions.set(k, v);
      }
    } catch {
      /* corrupt/unavailable storage → in-memory only */
    }
  }

  private persist(): void {
    if (!this.storage) return;
    try {
      const obj: Record<string, number> = {};
      for (const [k, v] of this.positions) obj[k] = v;
      this.storage.setItem(this.storageKey, JSON.stringify(obj));
    } catch {
      /* storage full/blocked → keep in-memory only */
    }
  }
}
